import React, { useState, useEffect } from "react";
import axios from "axios";

const LoanCustomersPage = () => {
  const [loanData, setLoanData] = useState([]);
  const [loading, setLoading] = useState(true);
  const baseURL = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    const accessToken = sessionStorage.getItem("access_token");
    axios
      .get(`${baseURL}/fetchLoanData`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then((response) => {
        setLoanData(response.data);
        console.log(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching loan data", error);
        setLoading(false);
      });
  }, [baseURL]);

  const calculatePendingAmount = (
    loan_emi_amount,
    rem_amount,
    value,
    daysCount,
    initialProfit,
    amount
  ) => {
    const currentDuration = parseInt(daysCount / value);
    const currEmiAmount = currentDuration * loan_emi_amount;
    const remAmount = Number(amount) - Number(rem_amount);
    const pending_amount = currEmiAmount - remAmount;
    return pending_amount >= 0 ? parseFloat(pending_amount.toFixed(2)) : 0;
  };

  const calculateDaysFromEndDate = (endDate) => {
    const end = new Date(endDate);
    const currentDate = new Date();
    const differenceInMs = end - currentDate;
    const pendingDays = Math.ceil(differenceInMs / (1000 * 60 * 60 * 24));
    return pendingDays >= 0 ? pendingDays : 0;
  };

  const calculateDaysFromEmiDate = (emiDate) => {
    const emi = new Date(emiDate);
    const currentDate = new Date();
    emi.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);
    const differenceInMs = currentDate - emi;
    if (differenceInMs === 0) {
      return 1;
    }
    const daysCount = Math.floor(differenceInMs / (1000 * 60 * 60 * 24)) + 1;
    return daysCount > 0 ? daysCount : 0;
  };

  const groupData = (data) => {
    const grouped = {};

    data.forEach((item) => {
      if (!grouped[item.name]) {
        grouped[item.name] = {};
      }

      if (!grouped[item.name][item.scheme_name]) {
        grouped[item.name][item.scheme_name] = {};
      }

      if (!grouped[item.name][item.scheme_name][item.loan_id]) {
        grouped[item.name][item.scheme_name][item.loan_id] = {
          name: item.name,
          scheme_name: item.scheme_name,
          tamilname: item.tamilname,
          total_emi_amount: 0,
          entries: [],
        };
      }

      grouped[item.name][item.scheme_name][item.loan_id].total_emi_amount +=
        Number(item.log_emi_amount);

      grouped[item.name][item.scheme_name][item.loan_id].entries.push(item);
    });

    return grouped;
  };

  const filteredData = loanData.filter(
    (item) =>
      item.scheme_name?.toLowerCase() === "daily collection" ||
      item.scheme_name?.toLowerCase() === "weekly collection"
  );

  const groupedData = groupData(filteredData);

  const renderSchemeTable = (agentId, scheme) => {
    const schemeData = groupedData[agentId][scheme];

    const totalEMIForScheme = Object.keys(schemeData)
      .reduce((sum, loanId) => {
        return sum + schemeData[loanId].total_emi_amount;
      }, 0)
      .toFixed(2);

    return (
      <div key={`${agentId}-${scheme}`}>
        <h3 className="font-semibold text-md text-center pt-1"> {scheme}</h3>
        <p className="font-semibold text-gray-700 text-center pt-3">
          Total EMI: ₹{totalEMIForScheme}
        </p>
        <table className="table-auto w-full border-collapse border border-gray-300 mt-4">
          <thead>
            <tr>
              <th
                className="px-2 py-1 text-sm border border-gray-500 text-left w-4"
                style={{ paddingTop: "4px", paddingBottom: "8px" }}
              >
                S.No
              </th>
              <th
                className="px-2 py-1 text-sm border border-gray-500 text-left w-4"
                style={{ paddingTop: "4px", paddingBottom: "8px" }}
              >
                EMI
              </th>
              <th
                className="px-2 py-1 text-sm border border-gray-500 text-left w-5"
                style={{ paddingTop: "4px", paddingBottom: "8px" }}
              >
                Customer Name
              </th>
              <th
                className="px-2 py-1 text-sm border border-gray-500 text-left w-4"
                style={{ paddingTop: "4px", paddingBottom: "8px" }}
              >
                Days
              </th>
              <th
                className="px-2 py-1 text-sm border border-gray-500 text-left w-4"
                style={{ paddingTop: "4px", paddingBottom: "8px" }}
              >
                Total Pending
              </th>
              <th
                className="px-2 py-1 text-sm border border-gray-500 text-left w-4"
                style={{ paddingTop: "4px", paddingBottom: "8px" }}
              >
                Pending
              </th>

              {[...Array(6)].map((_, i) => (
                <th
                  key={i}
                  className="px-2 py-1 border border-gray-500 text-sm w-8"
                  style={{ paddingTop: "4px", paddingBottom: "8px" }}
                ></th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(schemeData).map((loanId, index) => {
              const item = schemeData[loanId]; // Single loan entry
              const totalEMI = item.total_emi_amount.toFixed(2);
              const daysCount = calculateDaysFromEmiDate(
                item.entries[0].emi_date
              );
              const pendingAmount = calculatePendingAmount(
                Number(item.entries[0].loan_emi_amount),
                Number(item.entries[0].rem_amount),
                parseInt(item.entries[0].num),
                daysCount,
                Number(item.entries[0].initial_profit),
                Number(item.entries[0].amount)
              );
              const pendingDays = calculateDaysFromEndDate(
                item.entries[0].end_date
              );
              const textColor =
                pendingAmount === 0
                  ? "text-red-600 font-semibold"
                  : "text-black-900 font-semibold";

              const rowBackgroundColor =
                index % 2 === 0 ? "bg-gray-100" : "bg-white";

              return (
                <tr key={loanId} className={rowBackgroundColor}>
                  <td
                    className={`px-2 py-1 border border-gray-500 text-left w-4 ${textColor}`}
                    style={{ paddingTop: "4px", paddingBottom: "8px" }}
                  >
                    {index + 1}
                  </td>
                  <td
                    className={`px-2 py-1 border border-gray-500 text-left w-4 ${textColor}`}
                    style={{ paddingTop: "4px", paddingBottom: "8px" }}
                  >
                    {totalEMI}
                  </td>
                  <td
                    className={`px-2 py-1 border border-gray-500 text-left w-4 ${textColor}`}
                    style={{ paddingTop: "4px", paddingBottom: "8px" }}
                  >
                    {item.entries[0].tamilname}
                  </td>
                  <td
                    className={`px-2 py-1 border border-gray-500 text-left w-4 ${textColor}`}
                    style={{ paddingTop: "4px", paddingBottom: "8px" }}
                  >
                    {pendingDays}
                  </td>
                  <td
                    className={`px-2 py-1 border border-gray-500 text-left w-4 ${textColor}`}
                    style={{ paddingTop: "4px", paddingBottom: "8px" }}
                  >
                    {item.entries[0].rem_amount}
                  </td>
                  <td
                    className={`px-2 py-1 border border-gray-500 text-left w-4 ${textColor}`}
                    style={{ paddingTop: "4px", paddingBottom: "8px" }}
                  >
                    {pendingAmount}
                  </td>

                  {[...Array(6)].map((_, i) => (
                    <td
                      key={i}
                      className="px-2 py-1 border border-gray-500 text-left w-4"
                    ></td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTable = () => (
    <div>
      {Object.keys(groupedData).map((name) => (
        <div key={name} className="mb-8">
          <h2 className="font-bold text-lg mb-1 text-center">{name}</h2>

          {Object.keys(groupedData[name])
            .filter((scheme) => scheme.toLowerCase().includes("daily"))
            .map((scheme) => (
              <div key={scheme} className="mb-6">
                {renderSchemeTable(name, scheme)}
              </div>
            ))}

          {Object.keys(groupedData[name])
            .filter((scheme) => scheme.toLowerCase().includes("weekly"))
            .map((scheme) => (
              <div key={scheme} className="mb-6">
                {renderSchemeTable(name, scheme)}
              </div>
            ))}
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-lg font-semibold text-gray-600">Loading…</p>
      </div>
    );
  }

  return <div>{renderTable()}</div>;
};

export default LoanCustomersPage;
